import { useMemo } from "react";
import { createFileRoute, useParams, Link, Navigate } from "@tanstack/react-router";
import { ArrowLeft, LayoutGrid, CalendarRange } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useBoardBundle, useAllProfilesLite } from "@/hooks/use-kanban";
import { isBoardManager } from "@/lib/kanban";
import { PlannerView } from "@/components/kanban/PlannerView";

export const Route = createFileRoute("/_app/boards/$id_/timeline")({
  component: BoardTimelinePage,
});

function BoardTimelinePage() {
  const { id } = useParams({ from: "/_app/boards/$id_/timeline" });
  const { user, profile, role, loading: authLoading } = useAuth();
  const { data: bundle, isLoading } = useBoardBundle(id);
  const { data: allProfiles = [] } = useAllProfilesLite();

  const profileMap = useMemo(() => {
    const m = new Map<string, { full_name: string; avatar_url: string | null }>();
    allProfiles.forEach((p) =>
      m.set(p.id as string, {
        full_name: p.full_name as string,
        avatar_url: (p.avatar_url as string | null) ?? null,
      }),
    );
    return m;
  }, [allProfiles]);

  const fieldValuesByCard = useMemo(() => {
    const m = new Map<string, Record<string, string>>();
    (bundle?.fieldValues ?? []).forEach((v) => {
      if (v.value == null) return;
      const cur = m.get(v.card_id) ?? {};
      cur[v.field_id] = v.value;
      m.set(v.card_id, cur);
    });
    return m;
  }, [bundle?.fieldValues]);

  if (authLoading || isLoading || !user || !profile || !role) {
    return (
      <div className="kb-page-bg px-[22px] py-6">
        <div className="h-64 animate-pulse rounded-2xl bg-white/50" />
      </div>
    );
  }
  if (!bundle?.board) {
    return (
      <div className="kb-page-bg px-[22px] py-6">
        <div className="kb-glass p-6 text-[13px]">
          Board not found.{" "}
          <Link to="/boards" className="text-[#7C3AED] underline">
            Back
          </Link>
        </div>
      </div>
    );
  }

  const viewer = {
    role,
    userId: user?.id,
    isTeamLead: !!profile?.is_team_lead,
    department: profile?.department ?? null,
  };
  const isManager = isBoardManager(viewer, bundle.board);

  if (!isManager) {
    return <Navigate to="/boards/$id" params={{ id }} replace />;
  }

  return (
    <div className="kb-page-bg">
      <div className="px-4 pt-4 sm:px-[22px]">
        <div className="kb-glass flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div className="flex items-center gap-3">
            <Link to="/boards" className="rounded-full p-1.5 hover:bg-slate-100">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <h1 className="text-[17px] font-bold leading-tight text-slate-900">
                {bundle.board.name}
              </h1>
              <div className="text-[11px] text-slate-500">Assign work planner</div>
            </div>
          </div>
          <div className="inline-flex rounded-full border bg-white p-0.5">
            <Link
              to="/boards/$id"
              params={{ id }}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-100"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Columns
            </Link>
            <button
              type="button"
              className="kb-btn-primary inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold"
            >
              <CalendarRange className="h-3.5 w-3.5" /> Assign Work
            </button>
          </div>
        </div>
      </div>

      <PlannerView
        boardId={id}
        columns={bundle.columns}
        cards={bundle.cards}
        drafts={bundle.drafts}
        members={bundle.members}
        memberProfiles={profileMap}
        fieldDefs={bundle.fieldDefs}
        fieldValuesByCard={fieldValuesByCard}
        canPlan={true}
        currentUserId={user?.id}
      />
    </div>
  );
}
